/**
 * Converts markdown to HTML with GFM support.
 * Supports: headers, bold, italic, links, images, lists, code, blockquotes, and tables.
 */

function sanitizeUrl(url: string): string {
  // 2026-09-04 allowlist (websearch 2026 chatbot file-preview XSS: Open WebUI
  // CVE-2026-45318, Dify CVE-2026-42138, TypeBot CVE-2026-48768) — antes blocklist
  // (javascript:/data:/vbscript:) permitía blob:/file:/ftp:/etc. Ahora solo:
  // https?://, /relativa, #ancla, mailto:. Todo lo demás → "#".
  const raw = url.trim();
  if (!raw) return "#";
  try {
    // Browsers strip TAB/LF/CR when resolving URLs, so "java\nscript:" is
    // equivalent to "javascript:" — normalize whitespace before the check.
    const decoded = decodeURIComponent(raw)
      .replace(/[\t\n\r]/g, "")
      .trim();
    const lower = decoded.toLowerCase();
    if (
      lower.startsWith("javascript:") ||
      lower.startsWith("data:") ||
      lower.startsWith("vbscript:") ||
      lower.startsWith("blob:") ||
      lower.startsWith("file:") ||
      lower.startsWith("ftp:") ||
      decoded.includes("&#") ||
      decoded.includes("%00") ||
      decoded.includes("\0")
    ) {
      return "#";
    }
    // Allowlist final: https?://, /relative, #anchor, mailto:
    if (
      /^https?:\/\//i.test(decoded) ||
      decoded.startsWith("/") ||
      decoded.startsWith("#") ||
      lower.startsWith("mailto:")
    ) {
      return url;
    }
  } catch {
    // decodeURIComponent failed — treat as unsafe and block
    return "#";
  }
  return "#";
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * 2026-09-11 P1 heading slugs (SSOT compartido con TableOfContents): el
 * slugify anterior (`[^a-z0-9\s-]`) vaciaba títulos en zh/ja/ko/ar/ru
 * (ids "" rotos + anchors muertos). Unicode-aware: conserva letras/números
 * de cualquier script (\p{L}\p{N}); latinas se normalizan sin diacríticos
 * (Café → cafe) para URLs estables.
 */
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/**
 * Id único para un heading: primer uso el slug pelado, duplicados con
 * sufijo -1/-2 (convención GitHub), slug vacío → fallback `heading-{index}`.
 * ToC y markdown usan el MISMO seen-map en orden de documento para que los
 * anchors del índice coincidan con los ids del HTML.
 */
export function uniqueHeadingId(
  base: string,
  index: number,
  seen: Map<string, number>,
): string {
  const clean = base && base.trim() ? base : `heading-${index}`;
  const n = seen.get(clean) ?? 0;
  seen.set(clean, n + 1);
  return n === 0 ? clean : `${clean}-${n}`;
}

/** Texto plano para slug: quita tags HTML insertados + decodifica entidades. */
function plainTextForSlug(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function renderDocumentSheetHtml(rawContent: string): string {
  const sections = rawContent
    .trim()
    .split(/\n\s*\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  const renderedSections = sections.map((sec, idx) => {
    const lines = sec
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (idx === 0) {
      return `<div class="space-y-1 mb-4">${lines
        .map((l, i) =>
          i === 0
            ? `<div class="font-bold tracking-wide uppercase text-xs sm:text-sm text-foreground">${l}</div>`
            : `<div class="text-[11px] sm:text-xs text-foreground-soft/90 tracking-wide">${l}</div>`,
        )
        .join("")}</div>`;
    }
    if (idx === sections.length - 1 && sections.length > 1) {
      return `<div class="mt-auto pt-6 space-y-1 text-[11px] sm:text-xs font-semibold tracking-wider text-muted-foreground uppercase">${lines
        .map((l) => `<div>${l}</div>`)
        .join("")}</div>`;
    }
    return `<div class="my-4 space-y-1 text-[11px] sm:text-xs text-foreground/90 leading-[1.65]">${lines
      .map((l) => `<div>${l}</div>`)
      .join("")}</div>`;
  });

  return `<div class="my-8 not-prose" role="region" aria-label="Vista previa de documento"><div class="mx-auto max-w-lg rounded-2xl border border-border/80 bg-[var(--card)] p-4 sm:p-7 shadow-md transition-all"><div class="mb-3 flex items-center justify-between text-[11px] font-mono text-muted-foreground/80 px-1"><span class="inline-flex items-center gap-1.5 font-semibold text-foreground/80"><svg class="size-3.5 text-[var(--accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>Vista de Hoja Oficial (Carta / NTC 1486)</span><span class="hidden sm:inline text-[10px] text-muted-foreground/70">Márgenes 4-2-3-3 cm</span></div><div class="relative rounded-xl border border-dashed border-border/70 bg-[var(--background)]/60 px-5 py-8 sm:px-8 sm:py-10 text-center font-serif min-h-[380px] flex flex-col justify-between select-text shadow-inner"><div class="absolute top-1.5 left-1/2 -translate-x-1/2 text-[9px] font-mono text-muted-foreground/50 tracking-wider select-none">↑ 3,0 cm sup.</div><div class="absolute left-1.5 top-1/2 -translate-y-1/2 text-[9px] font-mono text-muted-foreground/50 tracking-wider -rotate-90 origin-center select-none hidden sm:block">← 4,0 cm enc.</div><div class="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] font-mono text-muted-foreground/50 tracking-wider rotate-90 origin-center select-none hidden sm:block">2,0 cm der. →</div><div class="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[9px] font-mono text-muted-foreground/50 tracking-wider select-none">↓ 3,0 cm inf.</div>${renderedSections.join("")}</div></div></div>`;
}

export function markdownToHtml(md: string): string {
  if (!md) return "";

  let html = md;

  // Escape HTML entities
  html = html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // GFM Tables — must run before other block-level processing
  html = convertGfmTables(html);

  // Document sheet blocks (```document ... ``` or ```sheet ... ```)
  html = html.replace(
    /```(?:document|sheet)\r?\n([\s\S]*?)```/g,
    (_match, content: string) => renderDocumentSheetHtml(content),
  );

  // Code blocks (``` ... ```)
  // 2026-09-21 FASE 3 FIXER design-docs-legal (research 2026 Mintlify/Stripe docs:
  // code sample = 1 superficie --radius-panel + bg-muted + text-foreground + mono,
  // sin segundo arcoíris ni superficies anidadas; paridad con docs-code-block y
  // .prose-custom pre — AA 4.5:1 con text-foreground explícito).
  // 2026-09-28 RUN 6 FASE 3 MD-01 (SEED=2026-09-28-eea43113, research 2026 W3C APG
  // "Developing a Keyboard Interface" (scroll containers): un `overflow-x: auto`
  // sin `tabindex="0"` NO es alcanzable por teclado — el ejemplo se desplazaba
  // solo con el ratón (WCAG 2.2 SC 2.1.1 + 2.4.7). Aquí no hay ResizeObserver
  // posible (HTML estático de servidor, no un componente cliente como
  // docs-code-block.tsx:106), así que el tabindex es incondicional: es el
  // patrón documentado para scrollables estáticos. Anillo focus-visible --accent
  // 2px = paridad exacta de la tabla BYOK (section-chat.tsx:332-336).
  // MD-05: `dir="ltr"` explícito — en /ar el <pre> heredaba dir="rtl" y el
  // ejemplo (`curl -H "Authorization: Bearer …"`, `const res = await fetch(…)`)
  // se reordenaba por bidi (MDN `direction` + Unicode Bidirectional Algorithm
  // UAX #9; precedente fechado del repo: chat-canvas.tsx:1699 dir="ltr").
  html = html.replace(
    /```([\s\S]*?)```/g,
    // 2026-10-03 RUN12 FASE 8 N20a [md-pre-step--1] (research 2026 Utopia: text-sm 14→step--1 14.55@768 Δ0.55; dir/tabindex/role intactos).
    '<pre dir="ltr" tabindex="0" role="region" aria-label="Code sample" class="rounded-[var(--radius-panel)] bg-[var(--muted)] border border-[var(--border)] p-4 overflow-x-auto text-[var(--step--1)] font-mono text-foreground my-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"><code dir="ltr">$1</code></pre>',
  );

  // Inline code (`code`)
  // 2026-10-03 RUN12 FASE 8 N20b [md-inline-code-step--2] (research 2026 Utopia: text-xs 12→step--2 11.94@768 Δ0.06).
  html = html.replace(
    /`([^`]+)`/g,
    '<code class="rounded px-1.5 py-0.5 bg-[var(--muted)] border border-[var(--border)] text-[var(--step--2)] font-mono text-[var(--accent)]">$1</code>',
  );

  // Bold (**text**)
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

  // Italic (*text*)
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");

  // Images (![alt](url)) — convert before links to avoid conflict
  // 2026-09-04 CLS: las imágenes de contenido no traen intrínsecas, así que se reserva
  // la caja (aspect-ratio 16/9 + object-fit contain sin recorte) y se difiere la carga.
  html = html.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    (_match, alt: string, url: string) => {
      const safeUrl = sanitizeUrl(url);
      return safeUrl
        ? `<img src="${escapeAttr(safeUrl)}" alt="${escapeAttr(alt)}" loading="lazy" decoding="async" class="rounded-lg my-4 max-w-full" style="width:100%;height:auto;aspect-ratio:16/9;object-fit:contain;background:var(--muted);" />`
        : /* v8 ignore start -- residual branch */
          /* v8 ignore next -- residual branch */
          "";
    },
  );

  // Links ([text](url))
  /* v8 ignore stop */
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_match, text: string, url: string) => {
      const safeUrl = sanitizeUrl(url);
      return safeUrl
        ? `<a href="${escapeAttr(safeUrl)}" class="text-[var(--accent)] hover:underline transition-colors">${text}</a>`
        : /* v8 ignore start -- residual branch */
          /* v8 ignore next -- residual branch */
          text;
    },
  );
  /* v8 ignore stop */

  // Headers — h2/h3 con id slug (paridad con TableOfContents: mismo
  // slugify + dedup en orden de documento vía seen-map). Una sola pasada
  // #{1,3} para que los duplicados se numeren en orden real (dos replaces
  // separados numerarían h3 y h2 en secuencias distintas y romperían anchors).
  // 2026-09-21 FASE 3 FIXER design-docs-legal (research 2026 single-h1 SEO/a11y
  // BOIA/Moz: 1 h1 por página — el h1 es el título del layout; `#` de markdown
  // se degrada a h2 con id para no duplicar h1 y mantener paridad ToC).
  const seenHeadingIds = new Map<string, number>();
  let headingIndex = 0;
  html = html.replace(
    /^(#{1,3}) (.*$)/gim,
    (_match: string, hashes: string, rawTitle: string) => {
      const level = hashes.length;
      const id = uniqueHeadingId(
        slugifyHeading(plainTextForSlug(rawTitle)),
        headingIndex++,
        seenHeadingIds,
      );
      const safeId = escapeAttr(id);
      // 2026-10-03 RUN12 FASE3 D2 `design-md-headings-fixed-scale` (research
      // 2026 Utopia fluid type scale: steps con clamp en vez de tallas fijas —
      // step-1 ≈ text-xl y step-0 ≈ text-lg a ancho desktop, fluidos fuera;
      // break-words paridad Hero h1 para títulos largos de/de-ja).
      if (level === 3) {
        return `<h3 id="${safeId}" class="text-[var(--step-0)] font-semibold text-foreground mt-8 mb-3 break-words">${rawTitle}</h3>`;
      }
      return `<h2 id="${safeId}" class="text-[var(--step-1)] font-semibold text-foreground mt-10 mb-4 break-words">${rawTitle}</h2>`;
    },
  );

  // Lists
  const lines = html.split("\n");
  let inList = false;
  const processed: string[] = [];

  for (const line of lines) {
    const listMatch = line.match(/^- (.*)$/);
    if (listMatch) {
      if (!inList) {
        // 2026-09-28 RUN 6 FASE 3 MD-02 (SEED=2026-09-28-eea43113, research 2026
        // MDN "Logical properties and values" + CSS Logical Properties L1 §4.2:
        // `margin-inline-start` mapea a margin-left en LTR y a margin-right en
        // RTL, mientras `margin-left` NO voltea — en /ar la viñeta flex sí se iba
        // al borde de lectura pero la sangría se quedaba a la izquierda y
        // colgaba fuera del texto. `ms-4` lógico, 16px idénticos en LTR.
        processed.push('<ul class="space-y-2 my-5 ms-4">');
        inList = true;
      }
      processed.push(
        `<li class="flex items-start gap-2 text-foreground-soft"><span class="mt-2 inline-block size-1 rounded-full bg-[var(--accent)] shrink-0"></span><span class="flex-1 min-w-0">${listMatch[1]}</span></li>`,
      );
    } else {
      if (inList) {
        processed.push("</ul>");
        inList = false;
      }
      processed.push(line);
    }
  }
  if (inList) processed.push("</ul>");
  html = processed.join("\n");

  // Horizontal rules (---, ***, ___)
  html = html.replace(/^(?:---|___|\*\*\*)\s*$/gm, "<hr />");

  // Blockquotes
  // 2026-09-28 RUN 6 FASE 3 MD-02 (research 2026 MDN logical properties +
  // CSS Logical Properties L1 §4.5/§4.7: `border-inline-start` + `padding-inline-start`
  // siguen el lado de lectura — con `border-l`/`pl` físicos la barra de acento
  // del blockquote quedaba al lado equivocado en /ar). `border-s-2` = 2px en el
  // inline-start; `ps-4` = 1rem de padding inline-start. Idéntico en LTR.
  html = html.replace(
    /^&gt; (.*$)/gim,
    '<blockquote class="border-s-2 border-[var(--accent)] ps-4 my-5 text-foreground-soft italic text-pretty break-words">$1</blockquote>',
  );

  // Paragraphs (wrap lines that aren't HTML tags)
  const finalLines = html.split("\n");
  const finalProcessed: string[] = [];
  let paraBuffer: string[] = [];

  const isHtmlLine = (s: string) => /^<\/?[a-z]/i.test(s.trim());
  const isEmpty = (s: string) => s.trim() === "";

  for (const line of finalLines) {
    if (isHtmlLine(line) || isEmpty(line)) {
      if (paraBuffer.length > 0) {
        finalProcessed.push(
          `<p class="text-foreground-soft leading-[1.65] my-4">${paraBuffer.join(" ")}</p>`,
        );
        paraBuffer = [];
      }
      if (!isEmpty(line)) finalProcessed.push(line);
    } else {
      paraBuffer.push(line);
    }
  }
  if (paraBuffer.length > 0) {
    finalProcessed.push(
      `<p class="text-foreground-soft leading-[1.65] my-4">${paraBuffer.join(" ")}</p>`,
    );
  }

  return finalProcessed.join("\n");
}

/**
 * Convert GFM pipe tables to styled HTML tables.
 * Expects standard GFM table syntax:
 *   | Col A | Col B |
 *   | ----- | ----- |
 *   | a1    | b1    |
 */
function convertGfmTables(input: string): string {
  const lines = input.split("\n");
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    // Detect table start: a line with | that isn't inside a code block
    const isTableRow = (line: string) => {
      const trimmed = line.trim();
      return (
        trimmed.startsWith("|") &&
        trimmed.endsWith("|") &&
        trimmed.includes("|")
      );
    };

    const isSeparatorRow = (line: string) => {
      const trimmed = line.trim();
      return /^\|[\s:]*-+[\s:]*(\|[\s:]*-+[\s:]*)*\|$/.test(trimmed);
    };

    /* v8 ignore next -- residual branch */
    if (
      isTableRow(lines[i] ?? "") &&
      i + 1 < lines.length &&
      isSeparatorRow(lines[i + 1] ?? "")
    ) {
      // Parse header row
      const parseCells = (line: string) =>
        line
          .trim()
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((c) => c.trim());

      /* v8 ignore next -- residual branch */
      const headers = parseCells(lines[i] ?? "");
      // Skip separator row
      i += 2;

      const bodyRows: string[][] = [];
      /* v8 ignore next 2 -- residual branch */
      while (i < lines.length && isTableRow(lines[i] ?? "")) {
        /* v8 ignore next -- residual branch */
        bodyRows.push(parseCells(lines[i] ?? ""));
        i++;
      }

      // Build HTML table
      // 2026-09-28 RUN 6 FASE 3 MD-01 (research 2026 W3C APG keyboard interface:
      // un contenedor `overflow-x: auto` sin `tabindex="0"` no se puede desplazar
      // con teclado — en 360px la tabla GFM de /blog/[slug] y /guides/[slug] sólo
      // se movía con el ratón). Misma paridad que el <pre> de arriba y que la
      // tabla BYOK de /docs (role=region + aria-label + tabIndex + anillo).
      // MD-02/MD-05: `text-start` lógico (el `text-left` físico no voltea) +
      // `dir="ltr"` en el wrapper — las celdas contienen identificadores ASCII
      // (`aiTokensPerDay`, `sk-…`) que en RTL se reordenan por bidi.
      // 2026-10-03 RUN12 FASE3 D7 `design-gfm-tables-nowrap-ltr` (research 2026
      // MDN `overflow-wrap` + `word-break`: el `whitespace-nowrap` del th
      // impedía envolver en ar/ja — fuera; paridad tabla BYOK
      // section-chat.tsx:338-371: tabla `break-words` + celda mono `break-all`;
      // scroll confinado + tabindex/role + `dir="ltr"` intactos, `text-start`
      // intacto. Los spans `código` se rinden aquí con la MISMA superficie
      // inline-code + `break-all` (el pase genérico posterior ya no los toca:
      // sin backticks restantes) para que identificadores largos partan en vez
      // de desbordar el scroller a 360px.
      const renderCellCode = (cell: string) =>
        cell.replace(
          /`([^`]+)`/g,
          '<code class="rounded px-1.5 py-0.5 bg-[var(--muted)] border border-[var(--border)] text-[var(--step--2)] font-mono text-[var(--accent)] break-all">$1</code>',
        );
      const tableLines: string[] = [];
      // 2026-10-03 RUN12 FASE 8 N20c [md-table-step--1] + N20d [md-table-code-step--2] (research 2026 Utopia: table text-sm 14→step--1 Δ0.55; cell code text-xs 12→step--2 Δ0.06; break-words/start/dir intactos).
      tableLines.push(
        '<div dir="ltr" tabindex="0" role="region" aria-label="Table" class="overflow-x-auto my-6 rounded-lg border border-[var(--border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"><table class="w-full text-[var(--step--1)] text-start break-words">',
      );
      tableLines.push("<thead>");
      tableLines.push(
        `<tr class="border-b border-[var(--border)] bg-[var(--muted)]/50">${headers.map((h) => `<th class="px-4 py-3 font-semibold text-foreground">${renderCellCode(h)}</th>`).join("")}</tr>`,
      );
      tableLines.push("</thead>");
      tableLines.push("<tbody>");
      for (const row of bodyRows) {
        tableLines.push(
          `<tr class="border-b border-[var(--border)] last:border-0 hover:bg-[var(--muted)]/30 transition-colors">${row.map((c) => `<td class="px-4 py-3 text-foreground-soft">${renderCellCode(c)}</td>`).join("")}</tr>`,
        );
      }
      tableLines.push("</tbody>");
      tableLines.push("</table></div>");
      result.push(tableLines.join("\n"));
    } else {
      /* v8 ignore next -- residual branch */
      result.push(lines[i] ?? "");
      i++;
    }
  }

  return result.join("\n");
}
